import type { HTMLAttributes, MouseEventHandler, Ref } from 'react'
import { cn } from '#core/utils/cn.ts'
import { Tooltip } from '#ynt-design-system/components/tooltip/index.ts'
import {
  Thumbnail,
  THUMBNAIL_SHAPE_CLASS,
  THUMBNAIL_SIZE_CLASS,
} from '#ynt-design-system/components/thumbnail/thumbnail.tsx'
import type {
  ThumbnailProps,
  ThumbnailShape,
  ThumbnailSize,
} from '#ynt-design-system/components/thumbnail/thumbnail.tsx'

export interface ThumbnailStackItem extends Pick<
  ThumbnailProps,
  'src' | 'swatch' | 'icon' | 'iconVariant' | 'color' | 'tone' | 'status'
> {
  name: string
}

export interface ThumbnailStackLabels {
  empty: string
  /** The stack's accessible name, from its items' names. */
  names: (names: string[]) => string
  /** Heads the "+N" chip's tooltip. */
  more: (count: number) => string
}

export interface ThumbnailStackProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  items: ThumbnailStackItem[]
  /** Thumbnails shown before the rest collapse into a "+N" chip. */
  max?: number
  size?: ThumbnailSize
  shape?: ThumbnailShape
  onClick?: MouseEventHandler<HTMLElement>
  labels: ThumbnailStackLabels
  ref?: Ref<HTMLElement>
}

const STACK_SIZE_CLASS: Record<ThumbnailSize, string> = {
  'extra-small': 'ynt-thumbnail-stack-extra-small',
  small: 'ynt-thumbnail-stack-small',
  medium: 'ynt-thumbnail-stack-medium',
  large: 'ynt-thumbnail-stack-large',
}

/**
 * Overlapping thumbnails for a group of items, capped at `max` plus a "+N" chip that lists who
 * it hides in a tooltip; the stack as a whole is one control.
 */
export function ThumbnailStack({
  items,
  max = 4,
  size = 'small',
  shape = 'rounded',
  onClick,
  labels,
  className,
  ref,
  'aria-label': ariaLabel,
  ...rest
}: ThumbnailStackProps) {
  const shown = items.length > max ? items.slice(0, max) : items
  const hidden = items.slice(shown.length)

  const label =
    ariaLabel ?? (items.length === 0 ? labels.empty : labels.names(items.map((item) => item.name)))

  const Root = onClick ? 'button' : 'span'

  return (
    <Root
      ref={ref as Ref<HTMLButtonElement & HTMLSpanElement>}
      type={onClick ? 'button' : undefined}
      role={onClick ? undefined : 'img'}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'ynt-thumbnail-stack',
        STACK_SIZE_CLASS[size],
        shape === 'circle' && 'ynt-thumbnail-stack-circle',
        onClick && 'ynt-thumbnail-stack-interactive',
        className,
      )}
      {...rest}
    >
      {items.length === 0 && (
        <Thumbnail color="neutral" size={size} shape={shape} aria-hidden="true" />
      )}
      {shown.map((item, index) => (
        <Thumbnail
          key={index}
          src={item.src}
          swatch={item.swatch}
          icon={item.icon}
          iconVariant={item.iconVariant}
          color={item.color}
          tone={item.tone}
          status={item.status}
          size={size}
          shape={shape}
          aria-hidden="true"
        />
      ))}
      {hidden.length > 0 && (
        <Tooltip
          label={labels.more(hidden.length)}
          values={hidden.map((item) => ({ label: item.name }))}
        >
          <span
            className={cn(
              'ynt-thumbnail',
              THUMBNAIL_SIZE_CLASS[size],
              THUMBNAIL_SHAPE_CLASS[shape],
              'ynt-thumbnail-overflow',
            )}
            aria-hidden="true"
          >
            <span className="ynt-thumbnail-label">+{hidden.length}</span>
          </span>
        </Tooltip>
      )}
    </Root>
  )
}

export default ThumbnailStack
